import FireStoreService from "@/services/fireStoreService";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import isBoolean from "lodash/isBoolean";
import isNil from "lodash/isNil";
import colorUtil from "@/assets/utils/color.js";

const RG_FB_COLLECTIONS = {
  RADAR_GRID_OBJECTS: "radar_grid_objects",
  AI_CAMERA_CONTROL: "ai_camera_control",
  CAMERA_MOVE_REQUEST: "camera_move_request",
  CAMERA_FOCUS_REQUEST: "camera_focus_request",
  RADAR_CAMERA_MASTER: "radar_camera_master",
};

const RG_RISK_LEVEL = {
  SAFE: 0,
};
const RG_RISK_LEVEL_CANCELED = 99;

const RG_RISK_LEVEL_THRESHOLD = {
  [RG_RISK_LEVEL.SAFE]: 0, // 有効なリスクレベルとして扱う下限
  MAX: 10, // 有効なリスクレベルとして扱う上限
};

const RADAR_GRID_OBJECT_RISK_LEVEL = {
  [RG_RISK_LEVEL.SAFE]: {
    label: "safe",
    labelJa: "安全",
    color: "#0ED463",
    blinkColor: "rgb(14, 212, 99)",
    brinkIconId: "rg-icon-blink-safe",
    brinkOtherKindIconId: "rg-icon-blink-target-safe",
    frameIconId: "rg-icon-frame-safe",
  },
};

const RG_RISK_LEVEL_FEEDBACK_CANCEL = {
  label: "cancel",
  labelJa: "自動判定に戻る",
  color: "#F2F2F2",
};

const TRACK_IMG = {
  SAFE: require("@/assets/images/radarGrid/track_safe.svg"),
};

const RADAR_GRID_OBJECT_KIND = {
  SHIP: "ship",
  CARGO: "cargo",
  CRANE: "crane",
  BARGE: "barge",
  TUG: "tug",
  PASSENGER: "passenger",
  FISHING: "fishing",
  BOAT: "boat",
  BUOY: "buoy",
  FLOATING: "floating",
  pleasure: "pleasure",
  AMBIGUOUS: "ambiguous",
};

const isValidRiskLevel = riskLevel => {
  const {
    [RG_RISK_LEVEL.SAFE]: SAFE_THRESHOLD,
    MAX: MAX_THRESHOLD,
  } = RG_RISK_LEVEL_THRESHOLD;
  return (
    isNumber(riskLevel) &&
    SAFE_THRESHOLD <= riskLevel &&
    riskLevel <= MAX_THRESHOLD &&
    riskLevel !== RG_RISK_LEVEL_CANCELED
  );
};

const parseRiskLevelByThreshold = riskLevel => {
  const { SAFE } = RG_RISK_LEVEL;
  // NOTE: 有効なリスクレベルではない場合はパース失敗としてnullを返す
  if (!isValidRiskLevel(riskLevel)) return null;

  return SAFE;
};

const isKindValid = objectKind => {
  return new Set(Object.values(RADAR_GRID_OBJECT_KIND)).has(objectKind);
};

const isTracked = objectKind => {
  return isString(objectKind) && objectKind.length > 0;
};

const getFrameIconId = (riskLevel, isKindValid = true) => {
  const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
  if (isKindValid) return riskLevelObject.frameIconId;
  return null;
};

const getBrinkIconId = (riskLevel, isKindValid = true) => {
  const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
  if (isKindValid) return riskLevelObject.brinkIconId;
  return riskLevelObject.brinkOtherKindIconId;
};

/**
 * @typedef {Object} RiskLevelManagerResult
 * @property {string} objectIcon - The calculated object icon.
 * @property {string} frameIconId - The calculated frame icon ID.
 * @property {string} brinkIconId - The calculated brink icon ID.
 * @property {boolean} hasManualAssessment - manual or auto
 * @property {number} autoRiskLevel - The auto calculated risk level.
 * @property {number} riskLevel - The calculated risk level.
 */
/**
 * RgRiskLevelManager is a function that calculates risk levels and icons based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {string|null} options.ai_r - The AI risk level.
 * @param {string|null} options.man_r - The manual risk level.
 * @param {string|null} options.k - The object kind.
 * @param {number|null} options.cog - The course over ground.
 * @returns {RiskLevelManagerResult} - An object containing the calculated risk levels and icons.
 */
const RgRiskLevelManager = (
  { ai_r: aiRiskLevel, man_r: manRiskLevel, k: kind, cog } = {
    ai_r: null,
    man_r: null,
    k: null,
    cog: null,
  }
) => {
  const hasManualAssessment = () => {
    return isValidRiskLevel(manRiskLevel);
  };
  const hasAiAssessment = () => {
    return isValidRiskLevel(aiRiskLevel);
  };
  const calcRiskLevel = () => {
    // if (hasManualAssessment()) return parseRiskLevelByThreshold(manRiskLevel);
    return calcAutoRiskLevel(aiRiskLevel); // 手動でない場合に自動評価を返す
  };
  // 自動評価計算
  const calcAutoRiskLevel = () => {
    return RG_RISK_LEVEL.SAFE;
  };
  const isValidCog = () => isNumber(cog) && cog >= 0 && cog <= 360;
  const isShip = k => k === RADAR_GRID_OBJECT_KIND.SHIP;
  const kindToIconType = k => {
    if (k === RADAR_GRID_OBJECT_KIND.BUOY) return "buoy";
    return "vessel";
  };
  const calcTrackIcon = () => {
    const iconType = kindToIconType(kind);
    const riskLevelLabel = RADAR_GRID_OBJECT_RISK_LEVEL[calcRiskLevel()].label;

    if (iconType === RADAR_GRID_OBJECT_KIND.BUOY)
      return `rg-target-object-${riskLevelLabel}-default`;

    if (isValidCog()) return `rg-${iconType}-${riskLevelLabel}`;
    return `rg-${iconType}-none-${riskLevelLabel}`;
  };
  const calcTargetIcon = () => {
    const riskLevelLabel = RADAR_GRID_OBJECT_RISK_LEVEL[calcRiskLevel()].label;
    if (hasManualAssessment()) {
      return `rg-target-object-manual-${riskLevelLabel}`;
    }
    return `rg-target-object-${riskLevelLabel}-default`;
  };
  const calcObjectIcon = () => {
    if (isKindValid(kind)) return calcTrackIcon();
    return calcTargetIcon();
  };
  const calcFrameIconId = () => {
    if (kind === "buoy") return "";
    if (hasManualAssessment()) return "";
    return getFrameIconId(calcRiskLevel(), isKindValid(kind));
  };
  const calcBrinkIconId = () => {
    if (kind === "buoy") return "";
    return getBrinkIconId(calcRiskLevel(), isKindValid(kind));
  };
  return {
    objectIcon: calcObjectIcon(),
    frameIconId: calcFrameIconId(),
    brinkIconId: calcBrinkIconId(),
    hasManualAssessment: hasManualAssessment(),
    autoRiskLevel: calcAutoRiskLevel(),
    riskLevel: calcRiskLevel(),
    isValidCog: isValidCog(),
    isShip: isShip(kind),
  };
};

const CAMERA_CTL_STATUS = {
  DISABLED: 0,
  AUTO: 1,
  MANUAL: 2,
};

const CAMERA_SWITCH_ON_DISABLED_STYLE = {
  color: "#FFFFFF",
  backgroundColor: "#2D9CDB",
  opacity: "0.4",
  cursor: "not-allowed",
};

const CAMERA_SWITCH_OFF_DISABLED_STYLE = {
  color: "#BDBDBD",
  backgroundColor: "#6C7178",
  opacity: "0.4",
  cursor: "not-allowed",
};

const CAMERA_SWITCH_ON_STYLE = {
  color: "#FFFFFF",
  backgroundColor: "#2D9CDB",
};

const CAMERA_SWITCH_OFF_STYLE = {
  color: "#BDBDBD",
  backgroundColor: "#6C7178",
};

const RG_PRIVILEGE_CATEGORY = "radar_grid";

const RG_PRIVILEGE_SUBJECTS = {
  VIEW_CAMERA: "view_camera",
  CONTROL_CAMERA: "control_camera",
  RATE_RISK: "rate_risk",
};

const RG_PRIVILEGE_TYPES = {
  READ: "read",
  WRITE: "write",
  DELETE: "delete",
  GRANT: "grant",
};

const RADAR_GRID_CAMERA_MASTER_ICON = {
  SELECTED: "fixed-point-camera-selected",
  UNSELECTED: "fixed-point-camera-unselected",
};

const RADAR_GRID_CAMERA_MASTER_PULSING_COLOR = {
  AUTO: "rgb(255, 255, 255)",
  MANUAL: "rgb(235,87,87)", // #EB5757
};

class RequestParamPostRadarGridRiskAssessments {
  constructor(
    radarDetectedObjectId,
    riskLevel,
    byAI,
    byPicture,
    byRoute,
    reason
  ) {
    this._radarDetectedObjectId = radarDetectedObjectId;
    this._riskLevel = riskLevel;
    this._byAI = byAI;
    this._byPicture = byPicture;
    this._byRoute = byRoute;
    this._reason = reason;
  }

  isValid() {
    return (
      this.isValidRadarDetectedObjectId() &&
      this.isValidRiskLevel() &&
      this.isValidByAI() &&
      this.isValidByPicture() &&
      this.isValidByRoute() &&
      this.isValidReason()
    );
  }

  isValidRadarDetectedObjectId() {
    return (
      isString(this._radarDetectedObjectId) &&
      0 < this._radarDetectedObjectId.length
    );
  }

  isValidRiskLevel() {
    return (
      Object.values(RG_RISK_LEVEL).includes(this._riskLevel) ||
      this._riskLevel === RG_RISK_LEVEL_CANCELED
    );
  }

  isValidByAI() {
    return isNil(this._byAI) || isBoolean(this._byAI);
  }

  isValidByPicture() {
    return isNil(this._byPicture) || isBoolean(this._byPicture);
  }

  isValidByRoute() {
    return isNil(this._byRoute) || isBoolean(this._byRoute);
  }

  isValidReason() {
    return isNil(this._reason) || isString(this._reason);
  }

  createPayload() {
    if (!this.isValid()) throw new Error("RiskAssessmentPayload is not valid!");
    return {
      radar_detected_object_id: this._radarDetectedObjectId,
      risk_level: this._riskLevel,
      by_ai: this._byAI,
      by_picture: this._byPicture,
      by_route: this._byRoute,
      reason: this._reason,
    };
  }
}

class ResponseCreateRadarGridRiskAssessments {
  constructor({ status, code, data, message }) {
    this._status = status;
    this._code = code;
    this._data = data;
    this._message = message;
    const {
      radarDetectedObjectId,
      riskLevel,
      byAI,
      byPicture,
      byRoute,
      reason,
      createdAt,
    } = this.parseResponse();
    this._radarDetectedObjectId = radarDetectedObjectId;
    this._riskLevel = riskLevel;
    this._byAI = byAI;
    this._byPicture = byPicture;
    this._byRoute = byRoute;
    this._reason = reason;
    this._createdAt = createdAt;
  }

  isSuccess() {
    return !!this._status;
  }

  parseResponse() {
    try {
      return {
        radarDetectedObjectId: this._data.radar_detected_object_id,
        riskLevel: this._data.risk_level,
        byAI: this._data.by_ai,
        byPicture: this._data.by_picture,
        byRoute: this._data.by_route,
        reason: this._data.reason,
        createdAt: this._data.created_at,
      };
    } catch (_e) {
      throw new Error("ResponseCreateRadarGridRiskAssessments is not valid!");
    }
  }

  getRiskLevel() {
    if (!this.isSuccess() || !this.isValid()) {
      throw new Error("ResponseCreateRadarGridRiskAssessments is not valid!");
    }
    return this._riskLevel;
  }

  isValid() {
    try {
      return (
        this.isValidRadarDetectedObjectId() &&
        this.isValidRiskLevel() &&
        this.isValidByAI() &&
        this.isValidByPicture() &&
        this.isValidByRoute() &&
        this.isValidReason() &&
        this.isValidCreatedAt()
      );
    } catch (_e) {
      return false;
    }
  }

  isValidRadarDetectedObjectId() {
    return isString(this._radarDetectedObjectId);
  }

  isValidRiskLevel() {
    return (
      Object.values(RG_RISK_LEVEL).includes(this._riskLevel) ||
      this._riskLevel === RG_RISK_LEVEL_CANCELED
    );
  }

  isValidByAI() {
    return isBoolean(this._byAI);
  }

  isValidByPicture() {
    return isBoolean(this._byPicture);
  }

  isValidByRoute() {
    return isBoolean(this._byRoute);
  }

  isValidReason() {
    return isString(this._reason);
  }

  isValidCreatedAt() {
    return isString(this._createdAt);
  }
}

const cameraPulsingCreator = color => {
  return function(map) {
    let size = 100;
    return {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),
      onAdd: function() {
        let canvas = document.createElement("canvas");
        canvas.width = this.width;
        canvas.height = this.height;
        this.context = canvas.getContext("2d", {
          willReadFrequently: true,
        });
      },

      render: function() {
        let duration = 3000;
        let t = (performance.now() % duration) / duration;

        let radius = (size / 2) * 0.3;
        let outerRadius = (size / 2) * 0.7 * t + radius;
        let context = this.context;

        context.clearRect(0, 0, this.width, this.height);
        context.beginPath();
        context.arc(
          this.width / 2,
          this.height / 2,
          outerRadius,
          0,
          Math.PI * 2
        );
        context.fillStyle = colorUtil.getColor(color, 1 - t);
        context.fill();

        this.data = context.getImageData(0, 0, this.width, this.height).data;

        map.triggerRepaint();

        return true;
      },
    };
  };
};

export default {
  data() {
    return {
      RG_FB_COLLECTIONS: RG_FB_COLLECTIONS,
      RG_RISK_LEVEL: RG_RISK_LEVEL,
      RG_RISK_LEVEL_CANCELED,
      RADAR_GRID_OBJECT_RISK_LEVEL: RADAR_GRID_OBJECT_RISK_LEVEL,
      RG_RISK_LEVEL_FEEDBACK_CANCEL,
      CAMERA_CTL_STATUS: CAMERA_CTL_STATUS,
      CAMERA_SWITCH_ON_DISABLED_STYLE: CAMERA_SWITCH_ON_DISABLED_STYLE,
      CAMERA_SWITCH_OFF_DISABLED_STYLE: CAMERA_SWITCH_OFF_DISABLED_STYLE,
      CAMERA_SWITCH_ON_STYLE: CAMERA_SWITCH_ON_STYLE,
      CAMERA_SWITCH_OFF_STYLE: CAMERA_SWITCH_OFF_STYLE,
      RG_PRIVILEGE_CATEGORY: RG_PRIVILEGE_CATEGORY,
      RG_PRIVILEGE_SUBJECTS: RG_PRIVILEGE_SUBJECTS,
      RG_PRIVILEGE_TYPES: RG_PRIVILEGE_TYPES,
      RADAR_GRID_CAMERA_MASTER_ICON: RADAR_GRID_CAMERA_MASTER_ICON,
    };
  },

  methods: {
    createRiskLevelStatusLabel() {
      const riskLevel = this.RG_RISK_LEVEL.SAFE;
      const riskLevelStatusObject = {
        trackIcon: TRACK_IMG.SAFE,
        trackNumber: {
          color: "",
        },
        cautionStyle: {
          opacity: 0.6,
        },
        dangerStyle: {
          opacity: 0.6,
        },
        safeStyle: {
          opacity: 0.6,
        },
      };
      let safeColor = this.RADAR_GRID_OBJECT_RISK_LEVEL[this.RG_RISK_LEVEL.SAFE]
        .color;
      riskLevelStatusObject.trackIcon = TRACK_IMG.SAFE;
      riskLevelStatusObject.safeStyle.color = safeColor;
      riskLevelStatusObject.trackNumber.color = safeColor;
      riskLevelStatusObject.safeStyle.opacity = 1;
      riskLevelStatusObject.cautionStyle.color = this.disabledColor;
      riskLevelStatusObject.cautionStyle.opacity = 0.3;
      riskLevelStatusObject.dangerStyle.color = this.disabledColor;
      riskLevelStatusObject.dangerStyle.opacity = 0.3;
      return riskLevelStatusObject;
    },

    getBrinkIconId,

    getBrinkIcon() {
      const riskLevel = this.RG_RISK_LEVEL.SAFE;
      const size = 80;
      const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
      return {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function() {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d", { willReadFrequently: true });
        },

        render: function() {
          let duration = 800;
          let t = (performance.now() % duration) / duration;

          let ctx = this.context;

          ctx.clearRect(0, 0, this.width, this.height);
          ctx.beginPath();
          ctx.lineWidth = 5;
          ctx.lineJoin = "round";
          //左上
          ctx.moveTo(35, 10);
          ctx.lineTo(10, 10);
          ctx.lineTo(10, 35);
          //左下
          ctx.moveTo(10, 45);
          ctx.lineTo(10, 70);
          ctx.lineTo(35, 70);
          //右上
          ctx.moveTo(45, 10);
          ctx.lineTo(70, 10);
          ctx.lineTo(70, 35);
          //右下
          ctx.moveTo(45, 70);
          ctx.lineTo(70, 70);
          ctx.lineTo(70, 45);

          ctx.strokeStyle = colorUtil.getColor(
            riskLevelObject.blinkColor,
            1 - t
          );
          ctx.stroke();

          this.data = ctx.getImageData(0, 0, this.width, this.height).data;

          return true;
        },
      };
    },

    getBrinkIconOfOtherKind() {
      const riskLevel = this.RG_RISK_LEVEL.SAFE;
      const size = 80;
      const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
      return {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function() {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d", { willReadFrequently: true });
        },

        render: function() {
          let duration = 800;
          let t = (performance.now() % duration) / duration;

          let ctx = this.context;

          ctx.clearRect(0, 0, this.width, this.height);
          ctx.beginPath();
          ctx.lineWidth = 3;

          ctx.arc(40, 40, 24, 0, 2 * Math.PI);

          ctx.strokeStyle = colorUtil.getColor(
            riskLevelObject.blinkColor,
            1 - t
          );
          ctx.stroke();

          this.data = ctx.getImageData(0, 0, this.width, this.height).data;

          return true;
        },
      };
    },

    getFrameIconId,

    getFrameIcon() {
      const riskLevel = this.RG_RISK_LEVEL.SAFE;
      const size = 80;
      const riskLevelObject = RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel];
      return {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function() {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d", { willReadFrequently: true });
        },

        render: function() {
          let ctx = this.context;

          ctx.clearRect(0, 0, this.width, this.height);
          ctx.beginPath();
          ctx.lineWidth = 5;
          ctx.lineJoin = "round";
          //左上
          ctx.moveTo(35, 10);
          ctx.lineTo(10, 10);
          ctx.lineTo(10, 35);
          //左下
          ctx.moveTo(10, 45);
          ctx.lineTo(10, 70);
          ctx.lineTo(35, 70);
          //右上
          ctx.moveTo(45, 10);
          ctx.lineTo(70, 10);
          ctx.lineTo(70, 35);
          //右下
          ctx.moveTo(45, 70);
          ctx.lineTo(70, 70);
          ctx.lineTo(70, 45);

          ctx.strokeStyle = riskLevelObject.blinkColor;
          ctx.stroke();

          this.data = ctx.getImageData(0, 0, this.width, this.height).data;

          return true;
        },
      };
    },

    createCameraPulsing: cameraPulsingCreator(
      RADAR_GRID_CAMERA_MASTER_PULSING_COLOR.AUTO
    ),

    createCameraPulsingManual: cameraPulsingCreator(
      RADAR_GRID_CAMERA_MASTER_PULSING_COLOR.MANUAL
    ),

    isKindValid,

    isTracked,

    async getRadarGridPrivilegeCollectionPath() {
      return `${await FireStoreService.getEnterpriseAccountPrivilegeDocumentPath()}/${RG_PRIVILEGE_CATEGORY}`;
    },

    parseRiskLevelByThreshold,

    isCaution(riskLevel) {
      return false;
    },

    isDanger(riskLevel) {
      return false;
    },

    isSafe(riskLevel) {
      return (
        this.parseRiskLevelByThreshold(riskLevel) === this.RG_RISK_LEVEL.SAFE
      );
    },

    initializeRequestParamPostRadarGridRiskAssessments(
      radarDetectedObjectId,
      riskLevel,
      { byAI, byPicture, byRoute, reason } = {}
    ) {
      return new RequestParamPostRadarGridRiskAssessments(
        radarDetectedObjectId,
        riskLevel,
        byAI,
        byPicture,
        byRoute,
        reason
      );
    },

    initializeResponseCreateRadarGridRiskAssessments(response) {
      return new ResponseCreateRadarGridRiskAssessments(response);
    },

    /**
     * Creates an instance of RgRiskLevelManager using the provided radarObjectFirestoreData.
     *
     * @param {Object} radarObjectFirestoreData - The data used to initialize the RgRiskLevelManager.
     * @returns {RiskLevelManagerResult} - The created RgRiskLevelManager instance.
     */
    createRgRiskLevelManager(radarObjectFirestoreData) {
      return new RgRiskLevelManager(radarObjectFirestoreData);
    },

    // /**
    //  * 指定されたオブジェクト種別に対応する日本語名を取得します。
    //  * @param {string} objectKind - オブジェクト種別（英語名）。
    //  * @returns {string} - 対応する日本語名。対応が見つからない場合は "不明" を返す。
    //  */
    // getObjectKindJa(objectKind) {
    //   const kindMapping = {
    //     ship: "船舶",
    //     cargo: "貨物船",
    //     crane: "クレーン船",
    //     barge: "バージ",
    //     tug: "タグボート",
    //     passenger: "旅客船",
    //     fishing: "漁船",
    //     boat: "小型船",
    //     buoy: "ブイ",
    //     ambiguous: "種別不明",
    //   };
    //
    //   return kindMapping[objectKind] || "---";
    // },
    // /**
    //  * オブジェクトの種類と認識率をフォーマットして文字列として返します。
    //  *
    //  * @param {string} kind - オブジェクトの種類を表す識別子。
    //  * @param {number|string} confidence - 認識率を表す数値または文字列（%単位）。
    //  * @returns {string} フォーマットされた種類名と認識率の文字列。
    //  *                   種類名が取得できない場合は "---" が使用され、
    //  *                   認識率が指定されていない場合も "---" となります。
    //  *                   例: "犬 (85%)", "--- (---%)"
    //  */
    // formatObjectRecognitionRate(kind, confidence) {
    //   const kindNameJa = this.getObjectKindJa(kind) || "---";
    //   const rate = confidence || "---";
    //   return `${kindNameJa} (${rate}%)`;
    // },
  },
};
